import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import SubscriptionGate from '../components/SubscriptionGate';
import Header from '../components/Header';
import { 
  Sparkles, 
  Moon, 
  Sun, 
  Star, 
  Eye,
  Heart,
  Zap,
  Gift,
  Loader2,
  MapPin,
  Clock,
  Save
} from 'lucide-react';

const CosmicCodes = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Numerology state
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [numerologyResults, setNumerologyResults] = useState(null);
  const [isLoadingNumerology, setIsLoadingNumerology] = useState(false);
  const [numerologyError, setNumerologyError] = useState('');

  // Astrology state
  const [astroFullName, setAstroFullName] = useState('');
  const [astroBirthDate, setAstroBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [timezone, setTimezone] = useState('');
  const [astrologyResults, setAstrologyResults] = useState(null);
  const [isLoadingAstrology, setIsLoadingAstrology] = useState(false);
  const [astrologyError, setAstrologyError] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  const handleNumerologyPDFDownload = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert('Please log in to download your report');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/numerology/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: fullName,
          birth_date: birthDate,
          numerology_data: numerologyResults
        })
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Numerology_Report_${fullName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleAstrologyPDFDownload = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        alert('Please log in to download your report');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/astrology/download-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: astroFullName,
          birth_date: astroBirthDate,
          birth_time: birthTime,
          birth_place: birthPlace,
          astrology_data: astrologyResults
        })
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Astrology_Report_${astroFullName.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleSaveToProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "Please log in to save to your profile"
        });
        return;
      }

      // Check if we have both astrology and numerology data
      if (!astrologyResults || !numerologyResults) {
        toast({
          variant: "destructive",
          title: "Incomplete Data",
          description: "Please generate both Astrology and Numerology reports first"
        });
        return;
      }

      // Prepare cosmic connection data with ALL detailed information
      const cosmicData = {
        // Astrology Data
        sun_sign: astrologyResults.sun_sign,
        moon_sign: astrologyResults.moon_sign,
        rising_sign: astrologyResults.rising_sign,
        sun_interpretation: astrologyResults.sun_interpretation,
        moon_interpretation: astrologyResults.moon_interpretation,
        rising_interpretation: astrologyResults.rising_interpretation,
        planets: astrologyResults.planets,
        chart_base64: astrologyResults.chart_base64,
        chart_svg: astrologyResults.chart_svg,
        house_cusps: astrologyResults.house_cusps,
        aspects: astrologyResults.aspects,
        sign_report: astrologyResults.sign_report,
        house_report: astrologyResults.house_report,
        natal_insights: astrologyResults.natal_insights,
        moon_phase: astrologyResults.moon_phase,
        ascendant_report: astrologyResults.ascendant_report,
        planetary_overview: astrologyResults.planetary_overview,
        house_cusps_overview: astrologyResults.house_cusps_overview,
        
        // Numerology Data
        life_path_number: numerologyResults.numbers.life_path,
        soul_urge_number: numerologyResults.numbers.soul_urge,
        expression_number: numerologyResults.numbers.expression,
        birthday_number: numerologyResults.numbers.birthday,
        
        // Detailed Numerology Interpretations
        life_path_interpretation: numerologyResults.interpretations.life_path,
        soul_urge_interpretation: numerologyResults.interpretations.soul_urge,
        expression_interpretation: numerologyResults.interpretations.expression,
        birthday_interpretation: numerologyResults.interpretations.birthday
      };

      const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/save-to-profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cosmicData)
      });

      if (!response.ok) {
        throw new Error('Failed to save to profile');
      }

      toast({
        title: "Success!",
        description: "Your Cosmic Connection has been saved to your Public Profile"
      });
    } catch (error) {
      console.error('Save to profile error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save to profile. Please try again."
      });
    }
  };

  const handleNumerologySubmit = async (e) => {
    e.preventDefault();
    setNumerologyError('');
    setIsLoadingNumerology(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setNumerologyError('Please log in to access Cosmic Codes');
        setIsLoadingNumerology(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/numerology`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          full_name: fullName,
          birth_date: birthDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate numerology reading');
      }

      const data = await response.json();
      setNumerologyResults(data);
    } catch (err) {
      console.error('Numerology error:', err);
      setNumerologyError(err.message || 'Failed to generate numerology reading. Please try again.');
    } finally {
      setIsLoadingNumerology(false);
    }
  };

  const handleAstrologySubmit = async (e) => {
    e.preventDefault();
    setAstrologyError('');
    setIsLoadingAstrology(true);

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setAstrologyError('Please log in to access Cosmic Codes');
        setIsLoadingAstrology(false);
        return;
      }

      // Geocode the birth place to get coordinates
      try {
        const geocodeResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(birthPlace)}&format=json&limit=1`
        );
        const geocodeData = await geocodeResponse.json();
        
        if (!geocodeData || geocodeData.length === 0) {
          throw new Error('Could not find location. Please enter a valid city and country (e.g., "New York, USA")');
        }

        const location = geocodeData[0];
        const lat = parseFloat(location.lat);
        const lon = parseFloat(location.lon);

        // Calculate timezone offset from longitude (rough approximation)
        // More accurate: use a timezone API, but this works for most cases
        const timezoneOffset = Math.round(lon / 15);

        const response = await fetch(`${BACKEND_URL}/api/cosmic-codes/astrology`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            full_name: astroFullName,
            birth_date: astroBirthDate,
            birth_time: birthTime,
            birth_place: birthPlace,
            latitude: lat,
            longitude: lon,
            timezone: timezoneOffset
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to generate astrology reading');
        }

        const data = await response.json();
        console.log('Astrology API Response:', {
          hasChartBase64: !!data.chart_base64,
          chartBase64Length: data.chart_base64?.length,
          hasChartSvg: !!data.chart_svg,
          chartSvgLength: data.chart_svg?.length
        });
        setAstrologyResults(data);
      } catch (geocodeError) {
        throw new Error(geocodeError.message || 'Failed to geocode location');
      }
    } catch (err) {
      console.error('Astrology error:', err);
      setAstrologyError(err.message || 'Failed to generate astrology reading. Please try again.');
    } finally {
      setIsLoadingAstrology(false);
    }
  };

  const numberIcons = {
    life_path: <Sparkles className="w-6 h-6" />,
    soul_urge: <Heart className="w-6 h-6" />,
    expression: <Zap className="w-6 h-6" />,
    birthday: <Gift className="w-6 h-6" />
  };

  return (
    <SubscriptionGate feature="cosmic_codes">
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-indigo-950 via-purple-900 to-slate-900 text-white">
        {/* Header Section */}
        <div className="relative overflow-hidden">
          {/* Starry background effect */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0ic3RhcnMiIHg9IjAiIHk9IjAiIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSIxIiBmaWxsPSJ3aGl0ZSIgb3BhY2l0eT0iMC4zIi8+PGNpcmNsZSBjeD0iMTUwIiBjeT0iODAiIHI9IjEuNSIgZmlsbD0id2hpdGUiIG9wYWNpdHk9IjAuNSIvPjxjaXJjbGUgY3g9IjgwIiBjeT0iMTUwIiByPSIwLjgiIGZpbGw9IndoaXRlIiBvcGFjaXR5PSIwLjQiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjc3RhcnMpIi8+PC9zdmc+')] opacity-20"></div>
          
          <div className="container mx-auto px-4 py-16 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <Moon className="w-10 h-10 text-purple-300 mr-3 animate-pulse" />
                <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-purple-200 via-pink-200 to-amber-200 bg-clip-text text-transparent">
                  Cosmic Codes
                </h1>
                <Sun className="w-10 h-10 text-amber-300 ml-3 animate-pulse" />
              </div>
              <p className="text-xl text-purple-200 mb-2">
                Sacred Numbers & Celestial Wisdom
              </p>
              <p className="text-md text-purple-300/80">
                Discover the mystical patterns woven into your existence
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 pb-16">
          
          {/* Numerology Section */}
          <div className="max-w-5xl mx-auto">
            <Card className="bg-gradient-to-br from-purple-900/40 to-indigo-900/40 border-purple-500/30 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center text-3xl text-amber-200">
                  <Star className="w-8 h-8 mr-3 text-amber-300" />
                  Tribal Numerology - The Sacred Math of the Ancestors
                </CardTitle>
                
                {/* Description Section */}
                <div className="mt-4 space-y-4 text-purple-200">
                  <div>
                    <h3 className="text-lg font-semibold text-amber-300 mb-2">Overview:</h3>
                    <p className="text-sm leading-relaxed">
                      Tribal Numerology is the study of numbers as sacred vibrations—cosmic tones that guided ancient peoples in naming, journeying, and understanding their destiny. Unlike modern numerology that's often commercialized, Tribal Numerology is rooted in ancestral science, indigenous symbolism, and spiritual storytelling.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-amber-300 mb-2">Core Concepts:</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="leading-relaxed">
                        <span className="font-semibold text-amber-200">Life Path Number:</span> Derived from the birthdate, this number reflects your soul's journey in this life. Among tribes, dates of birth were sacred rites of passage—coded reminders of your duty to the land, clan, and spirit.
                      </li>
                      <li className="leading-relaxed">
                        <span className="font-semibold text-amber-200">Name Numerology (Surname Codes):</span> Each surname carries ancestral math—whether measured in syllables, vowels, or tonal sounds. These numbers were used in tribal initiation rites, symbolizing family roles and inter-tribal positioning.
                      </li>
                      <li className="leading-relaxed">
                        <span className="font-semibold text-amber-200">Master Numbers (11, 22, 33):</span> Regarded as spiritual conduits between the realms—carriers of ancestral gifts and divine contracts.
                      </li>
                    </ul>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!numerologyResults ? (
                  <form onSubmit={handleNumerologySubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="fullName" className="text-purple-200">Full Birth Name</Label>
                        <Input
                          id="fullName"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          placeholder="e.g., Sarah Running River"
                          required
                          className="bg-slate-800/50 border-purple-500/30 text-white placeholder:text-gray-400"
                        />
                        <p className="text-xs text-purple-300/70">Use your complete name as it appears on your birth certificate</p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="birthDate" className="text-purple-200">Birth Date</Label>
                        <Input
                          id="birthDate"
                          type="text"
                          value={birthDate}
                          onChange={(e) => setBirthDate(e.target.value)}
                          placeholder="MM/DD/YYYY"
                          required
                          className="bg-slate-800/50 border-purple-500/30 text-white placeholder:text-gray-400"
                        />
                        <p className="text-xs text-purple-300/70">Format: Month/Day/Year (e.g., 03/15/1990)</p>
                      </div>
                    </div>

                    {numerologyError && (
                      <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-200">
                        {numerologyError}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isLoadingNumerology}
                      className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-lg py-6"
                    >
                      {isLoadingNumerology ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Channeling Ancestral Wisdom...
                        </>
                      ) : (
                        <>
                          <Eye className="w-5 h-5 mr-2" />
                          Reveal My Sacred Numbers
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-8">
                    {/* Results Header */}
                    <div className="text-center border-b border-purple-500/30 pb-6">
                      <h3 className="text-2xl font-bold text-amber-200 mb-2">Your Cosmic Blueprint</h3>
                      <p className="text-purple-200">Sacred numbers revealed for {fullName}</p>
                    </div>

                    {/* Numbers Grid */}
                    {Object.entries(numerologyResults.interpretations).map(([key, data]) => (
                      <Card key={key} className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-purple-500/20">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center text-2xl text-amber-200">
                              <div className="p-3 bg-purple-500/20 rounded-full mr-4">
                                {numberIcons[key]}
                              </div>
                              {data.display_name}
                            </CardTitle>
                            <div className="text-5xl font-bold text-purple-300">
                              {data.number}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {/* Meaning */}
                          <div>
                            <h4 className="text-lg font-semibold text-purple-200 mb-2 flex items-center">
                              <Star className="w-4 h-4 mr-2" />
                              Meaning
                            </h4>
                            <p className="text-gray-300 leading-relaxed">{data.meaning}</p>
                          </div>

                          {/* Tribal Archetype */}
                          {data.tribal_archetype && (
                            <div className="bg-amber-900/20 p-4 rounded-lg border border-amber-500/20">
                              <h4 className="text-lg font-semibold text-amber-200 mb-2 flex items-center">
                                <Zap className="w-4 h-4 mr-2" />
                                Tribal Archetype
                              </h4>
                              <p className="text-gray-300 leading-relaxed">{data.tribal_archetype}</p>
                            </div>
                          )}

                          {/* Ancestral Connection */}
                          {data.ancestral_connection && (
                            <div>
                              <h4 className="text-lg font-semibold text-purple-200 mb-2 flex items-center">
                                <Heart className="w-4 h-4 mr-2" />
                                Ancestral Connection
                              </h4>
                              <p className="text-gray-300 leading-relaxed">{data.ancestral_connection}</p>
                            </div>
                          )}

                          {/* Personal Growth */}
                          {data.personal_growth && (
                            <div className="bg-green-900/20 p-4 rounded-lg border border-green-500/20">
                              <h4 className="text-lg font-semibold text-green-200 mb-2 flex items-center">
                                <Sparkles className="w-4 h-4 mr-2" />
                                Path to Growth
                              </h4>
                              <p className="text-gray-300 leading-relaxed">{data.personal_growth}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}

                    {/* Action Buttons */}
                    <div className="flex gap-4 justify-center pt-6 border-t border-purple-500/30">
                      <Button
                        onClick={handleNumerologyPDFDownload}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        <Gift className="w-5 h-5 mr-2" />
                        Download Report (PDF)
                      </Button>
                      <Button
                        onClick={() => setNumerologyResults(null)}
                        variant="outline"
                        className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                      >
                        Generate New Reading
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Comprehensive Astrology Generator */}
            <Card className="bg-gradient-to-br from-indigo-900/40 to-blue-900/40 border-blue-500/30 backdrop-blur-sm mt-12">
              <CardHeader>
                <CardTitle className="flex items-center text-3xl text-blue-200">
                  <Moon className="w-8 h-8 mr-3 text-blue-300" />
                  Tribal Astrology: The Cosmic Drumbeat of the Ancestors
                </CardTitle>
                
                {/* Description Section */}
                <div className="mt-4 space-y-4 text-blue-200">
                  <div>
                    <h3 className="text-lg font-semibold text-blue-300 mb-2">Overview:</h3>
                    <p className="text-sm leading-relaxed">
                      Before modern star maps, our ancestors looked to the constellations for guidance, prophecy, and planting seasons. Tribal Astrology honors celestial wisdom passed down through oral tradition, dream interpretation, and natural observation.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-blue-300 mb-2">Core Features:</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="leading-relaxed">
                        <span className="font-semibold text-blue-100">Sun Sign (Day Energy):</span> Represents the fire of your birth—the archetypal role you're here to embody (e.g., the Jaguar, the Serpent, the Turtle, the Falcon).
                      </li>
                      <li className="leading-relaxed">
                        <span className="font-semibold text-blue-100">Moon Sign (Spirit Mirror):</span> Tied to your emotional body and spiritual heritage. Often matched with tribal rites of passage, and women's cycles of power.
                      </li>
                      <li className="leading-relaxed">
                        <span className="font-semibold text-blue-100">Rising Sign (Mask of the Ancestor):</span> Reflects your ancestral aura—the "first impression" you give and the tribal mask you wear to navigate this world.
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-blue-300 mb-2">Cosmic Tribal Elements:</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="leading-relaxed">
                        <span className="font-semibold text-blue-100">Elemental Balance:</span> Fire (Warrior clans), Earth (Healer clans), Water (Dreamers and Seers), Air (Messenger clans)
                      </li>
                      <li className="leading-relaxed">
                        <span className="font-semibold text-blue-100">Constellation Guardians:</span> Each tribe may align itself with a specific star family (e.g., Pleiades, Orion, Sirius)—believing their people descended from or were guided by them.
                      </li>
                    </ul>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!astrologyResults ? (
                  <form onSubmit={handleAstrologySubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="astroFullName" className="text-blue-200">Full Birth Name</Label>
                        <Input
                          id="astroFullName"
                          value={astroFullName}
                          onChange={(e) => setAstroFullName(e.target.value)}
                          placeholder="e.g., Sarah Running River"
                          required
                          className="bg-slate-800/50 border-blue-500/30 text-white placeholder:text-gray-400"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="astroBirthDate" className="text-blue-200">Birth Date</Label>
                        <Input
                          id="astroBirthDate"
                          type="text"
                          value={astroBirthDate}
                          onChange={(e) => setAstroBirthDate(e.target.value)}
                          placeholder="MM/DD/YYYY"
                          required
                          className="bg-slate-800/50 border-blue-500/30 text-white placeholder:text-gray-400"
                        />
                        <p className="text-xs text-blue-300/70">Format: Month/Day/Year (e.g., 03/15/1990)</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="birthTime" className="text-blue-200 flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Birth Time
                        </Label>
                        <Input
                          id="birthTime"
                          type="text"
                          value={birthTime}
                          onChange={(e) => setBirthTime(e.target.value)}
                          placeholder="HH:MM (24-hour format)"
                          required
                          className="bg-slate-800/50 border-blue-500/30 text-white placeholder:text-gray-400"
                        />
                        <p className="text-xs text-blue-300/70">24-hour format: e.g., 14:30 for 2:30 PM</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="birthPlace" className="text-blue-200 flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          Birth Place
                        </Label>
                        <Input
                          id="birthPlace"
                          value={birthPlace}
                          onChange={(e) => setBirthPlace(e.target.value)}
                          placeholder="City, Country"
                          required
                          className="bg-slate-800/50 border-blue-500/30 text-white placeholder:text-gray-400"
                        />
                        <p className="text-xs text-blue-300/70">e.g., New York, USA or London, UK</p>
                      </div>
                    </div>

                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-blue-200 text-sm flex items-center">
                        <Sparkles className="w-4 h-4 mr-2" />
                        We'll automatically calculate your location coordinates and timezone based on your birth place.
                      </p>
                    </div>

                    {astrologyError && (
                      <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-200">
                        {astrologyError}
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={isLoadingAstrology}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-lg py-6"
                    >
                      {isLoadingAstrology ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Reading the Stars...
                        </>
                      ) : (
                        <>
                          <Moon className="w-5 h-5 mr-2" />
                          Generate My Complete Natal Chart
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-8">
                    {/* Results Header */}
                    <div className="text-center border-b border-blue-500/30 pb-6">
                      <h3 className="text-2xl font-bold text-blue-200 mb-2">Your Celestial Blueprint</h3>
                      <p className="text-blue-300">Complete natal chart for {astroFullName}</p>
                    </div>

                    {/* Big Three */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/40 border-amber-500/30">
                        <CardContent className="pt-6 text-center">
                          <Sun className="w-12 h-12 text-amber-300 mx-auto mb-3" />
                          <h4 className="text-xl font-bold text-amber-200 mb-2">Sun Sign</h4>
                          <p className="text-3xl font-bold text-white">{astrologyResults.sun_sign}</p>
                          <p className="text-sm text-amber-300/80 mt-2">Your core essence & identity</p>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border-indigo-500/30">
                        <CardContent className="pt-6 text-center">
                          <Moon className="w-12 h-12 text-indigo-300 mx-auto mb-3" />
                          <h4 className="text-xl font-bold text-indigo-200 mb-2">Moon Sign</h4>
                          <p className="text-3xl font-bold text-white">{astrologyResults.moon_sign}</p>
                          <p className="text-sm text-indigo-300/80 mt-2">Your emotions & inner world</p>
                        </CardContent>
                      </Card>

                      <Card className="bg-gradient-to-br from-pink-900/40 to-rose-900/40 border-pink-500/30">
                        <CardContent className="pt-6 text-center">
                          <Star className="w-12 h-12 text-pink-300 mx-auto mb-3" />
                          <h4 className="text-xl font-bold text-pink-200 mb-2">Rising Sign</h4>
                          <p className="text-3xl font-bold text-white">{astrologyResults.rising_sign}</p>
                          <p className="text-sm text-pink-300/80 mt-2">Your outer personality</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Natal Chart Image - Fixed Rendering */}
                    {(astrologyResults.chart_base64 || astrologyResults.chart_svg) && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4 text-center">Your Natal Wheel Chart</h4>
                        <div className="flex justify-center">
                          {astrologyResults.chart_svg ? (
                            <div 
                              className="max-w-full rounded-lg bg-white p-4"
                              style={{ maxHeight: '600px', maxWidth: '600px' }}
                              dangerouslySetInnerHTML={{ __html: astrologyResults.chart_svg }}
                            />
                          ) : astrologyResults.chart_base64 ? (
                            <img 
                              src={`data:image/svg+xml;base64,${astrologyResults.chart_base64}`}
                              alt="Natal Chart"
                              className="max-w-full h-auto rounded-lg bg-white p-4"
                              style={{ maxHeight: '600px', maxWidth: '600px' }}
                            />
                          ) : (
                            <div className="text-center text-gray-400 p-8">
                              <p>Chart visualization unavailable</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* AI-Generated Sun, Moon & Rising Sign Descriptions */}
                    {(astrologyResults.sun_interpretation || astrologyResults.moon_interpretation || astrologyResults.rising_interpretation) && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4 flex items-center">
                          <Sparkles className="w-6 h-6 mr-2" />
                          Your Luminaries: Sun, Moon & Ascendant
                        </h4>
                        
                        {/* Sun Sign Interpretation */}
                        {astrologyResults.sun_interpretation && (
                          <div className="mb-6 bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/20 rounded-lg p-5">
                            <div className="flex items-center gap-3 mb-3">
                              <Sun className="w-8 h-8 text-amber-300" />
                              <h5 className="text-xl font-semibold text-amber-200">
                                Sun in {astrologyResults.sun_sign}
                              </h5>
                            </div>
                            <p className="text-gray-300 leading-relaxed">{astrologyResults.sun_interpretation}</p>
                          </div>
                        )}

                        {/* Moon Sign Interpretation */}
                        {astrologyResults.moon_interpretation && (
                          <div className="mb-6 bg-gradient-to-br from-indigo-900/20 to-purple-900/20 border border-indigo-500/20 rounded-lg p-5">
                            <div className="flex items-center gap-3 mb-3">
                              <Moon className="w-8 h-8 text-indigo-300" />
                              <h5 className="text-xl font-semibold text-indigo-200">
                                Moon in {astrologyResults.moon_sign}
                              </h5>
                            </div>
                            <p className="text-gray-300 leading-relaxed">{astrologyResults.moon_interpretation}</p>
                          </div>
                        )}

                        {/* Rising Sign Interpretation */}
                        {astrologyResults.rising_interpretation && (
                          <div className="bg-gradient-to-br from-pink-900/20 to-rose-900/20 border border-pink-500/20 rounded-lg p-5">
                            <div className="flex items-center gap-3 mb-3">
                              <Star className="w-8 h-8 text-pink-300" />
                              <h5 className="text-xl font-semibold text-pink-200">
                                Rising Sign (Ascendant) in {astrologyResults.rising_sign}
                              </h5>
                            </div>
                            <p className="text-gray-300 leading-relaxed">{astrologyResults.rising_interpretation}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Natal Insights - Visual Elements & Modes */}
                    {astrologyResults.natal_insights && Object.keys(astrologyResults.natal_insights).length > 0 && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4 flex items-center">
                          <Eye className="w-6 h-6 mr-2" />
                          Natal Insights
                        </h4>
                        
                        {/* Elements Distribution with Visual Icons */}
                        {astrologyResults.natal_insights.elements_data && (
                          <div className="mb-6">
                            <h5 className="text-lg font-semibold text-blue-300 mb-3">Elemental Balance</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {astrologyResults.natal_insights.elements_data.map((element, index) => {
                                const elementColors = {
                                  Fire: { bg: 'from-red-900/40 to-orange-900/40', border: 'border-red-500/30', icon: '🔥', text: 'text-red-300' },
                                  Earth: { bg: 'from-green-900/40 to-emerald-900/40', border: 'border-green-500/30', icon: '🌍', text: 'text-green-300' },
                                  Air: { bg: 'from-cyan-900/40 to-sky-900/40', border: 'border-cyan-500/30', icon: '💨', text: 'text-cyan-300' },
                                  Water: { bg: 'from-blue-900/40 to-indigo-900/40', border: 'border-blue-500/30', icon: '💧', text: 'text-blue-300' }
                                };
                                const style = elementColors[element.name] || elementColors.Fire;
                                
                                return (
                                  <div key={index} className={`bg-gradient-to-br ${style.bg} border ${style.border} rounded-lg p-4 text-center`}>
                                    <div className="text-4xl mb-2">{style.icon}</div>
                                    <h6 className={`font-semibold ${style.text} mb-1`}>{element.name}</h6>
                                    <div className="text-2xl font-bold text-white">{element.percentage.toFixed(1)}%</div>
                                    <div className="mt-2 bg-slate-800/50 rounded-full h-2">
                                      <div 
                                        className={`h-full rounded-full ${style.bg}`}
                                        style={{ width: `${element.percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Modes Distribution */}
                        {astrologyResults.natal_insights.modes_data && (
                          <div className="mb-6">
                            <h5 className="text-lg font-semibold text-blue-300 mb-3">Modal Qualities</h5>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {astrologyResults.natal_insights.modes_data.map((mode, index) => {
                                const modeColors = {
                                  Cardinal: { bg: 'from-purple-900/40 to-pink-900/40', border: 'border-purple-500/30', icon: '⚡', text: 'text-purple-300' },
                                  Fixed: { bg: 'from-amber-900/40 to-yellow-900/40', border: 'border-amber-500/30', icon: '🛡️', text: 'text-amber-300' },
                                  Mutable: { bg: 'from-teal-900/40 to-cyan-900/40', border: 'border-teal-500/30', icon: '🌀', text: 'text-teal-300' }
                                };
                                const style = modeColors[mode.name] || modeColors.Cardinal;
                                
                                return (
                                  <div key={index} className={`bg-gradient-to-br ${style.bg} border ${style.border} rounded-lg p-4 text-center`}>
                                    <div className="text-4xl mb-2">{style.icon}</div>
                                    <h6 className={`font-semibold ${style.text} mb-1`}>{mode.name}</h6>
                                    <div className="text-2xl font-bold text-white">{mode.percentage.toFixed(1)}%</div>
                                    <div className="mt-2 bg-slate-800/50 rounded-full h-2">
                                      <div 
                                        className={`h-full rounded-full ${style.bg}`}
                                        style={{ width: `${mode.percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Other Insights */}
                        <div className="space-y-3 text-gray-300 leading-relaxed">
                          {Object.entries(astrologyResults.natal_insights).filter(([key]) => 
                            !['elements_data', 'modes_data'].includes(key)
                          ).map(([key, value], index) => (
                            <div key={index} className="bg-slate-800/40 p-4 rounded-lg">
                              <p className="text-blue-300 font-semibold mb-2 capitalize">{key.replace(/_/g, ' ')}</p>
                              <p>{typeof value === 'object' ? (value.description || JSON.stringify(value)) : value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sign Report */}
                    {astrologyResults.sign_report && Object.keys(astrologyResults.sign_report).length > 0 && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4 flex items-center">
                          <Star className="w-6 h-6 mr-2" />
                          Zodiac Sign Report
                        </h4>
                        <div className="space-y-3 text-gray-300 leading-relaxed">
                          {Object.entries(astrologyResults.sign_report).map(([key, value], index) => (
                            <div key={index} className="bg-slate-800/40 p-4 rounded-lg">
                              <p className="text-blue-300 font-semibold mb-2 capitalize">{key.replace(/_/g, ' ')}</p>
                              <p>{typeof value === 'object' ? (value.description || JSON.stringify(value)) : value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* House Report */}
                    {astrologyResults.house_report && Object.keys(astrologyResults.house_report).length > 0 && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4">House Report</h4>
                        <div className="space-y-3 text-gray-300 leading-relaxed">
                          {Object.entries(astrologyResults.house_report).map(([key, value], index) => (
                            <div key={index} className="bg-slate-800/40 p-4 rounded-lg">
                              <p className="text-blue-300 font-semibold mb-2 capitalize">{key.replace(/_/g, ' ')}</p>
                              <p>{typeof value === 'object' ? (value.description || JSON.stringify(value)) : value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Ascendant Report */}
                    {astrologyResults.ascendant_report && Object.keys(astrologyResults.ascendant_report).length > 0 && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4">Ascendant (Rising Sign) Report</h4>
                        <div className="space-y-3 text-gray-300 leading-relaxed">
                          {Object.entries(astrologyResults.ascendant_report).map(([key, value], index) => (
                            <div key={index} className="bg-slate-800/40 p-4 rounded-lg">
                              <p className="text-blue-300 font-semibold mb-2 capitalize">{key.replace(/_/g, ' ')}</p>
                              <p>{typeof value === 'object' ? (value.description || JSON.stringify(value)) : value}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Planetary Positions with Zodiac Symbols */}
                    <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                      <h4 className="text-xl font-semibold text-blue-200 mb-4">Planetary Positions</h4>
                      
                      {/* LLM-Generated Overview */}
                      {astrologyResults.planetary_overview && (
                        <div className="mb-6 bg-gradient-to-br from-purple-900/20 to-indigo-900/20 border border-purple-500/20 rounded-lg p-5">
                          <div className="flex items-center gap-3 mb-3">
                            <Sparkles className="w-6 h-6 text-purple-300" />
                            <h5 className="text-lg font-semibold text-purple-200">Planetary Overview</h5>
                          </div>
                          <p className="text-gray-300 leading-relaxed">{astrologyResults.planetary_overview}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {astrologyResults.planets.slice(0, 12).map((planet, index) => {
                          // Zodiac symbols mapping
                          const zodiacSymbols = {
                            'Aries': '♈', 'Taurus': '♉', 'Gemini': '♊', 'Cancer': '♋',
                            'Leo': '♌', 'Virgo': '♍', 'Libra': '♎', 'Scorpio': '♏',
                            'Sagittarius': '♐', 'Capricorn': '♑', 'Aquarius': '♒', 'Pisces': '♓'
                          };
                          
                          // Planet symbols mapping
                          const planetSymbols = {
                            'Sun': '☉', 'Moon': '☽', 'Mercury': '☿', 'Venus': '♀',
                            'Mars': '♂', 'Jupiter': '♃', 'Saturn': '♄', 'Uranus': '♅',
                            'Neptune': '♆', 'Pluto': '♇', 'Ascendant': 'AC', 'Midheaven': 'MC'
                          };
                          
                          const zodiacSymbol = zodiacSymbols[planet.sign] || '';
                          const planetSymbol = planetSymbols[planet.name] || planet.name.charAt(0);
                          
                          return (
                            <div key={index} className="bg-slate-800/40 p-4 rounded-lg hover:bg-slate-800/60 transition-colors">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-2xl">{planetSymbol}</span>
                                  <span className="font-semibold text-blue-200">{planet.name}</span>
                                </div>
                                {planet.retrograde && (
                                  <span className="text-xs bg-red-500/20 text-red-300 px-2 py-1 rounded">℞</span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-3xl">{zodiacSymbol}</span>
                                <p className="text-white text-lg font-medium">{planet.sign}</p>
                              </div>
                              <p className="text-sm text-gray-400">House {planet.house} • {planet.element}</p>
                              <p className="text-xs text-gray-500 mt-1">{planet.degree}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* House Cusps with Zodiac Symbols */}
                    {astrologyResults.house_cusps && astrologyResults.house_cusps.length > 0 && (
                      <div className="bg-slate-900/60 p-6 rounded-lg border border-blue-500/20">
                        <h4 className="text-xl font-semibold text-blue-200 mb-4">House Cusps</h4>
                        
                        {/* LLM-Generated Overview */}
                        {astrologyResults.house_cusps_overview && (
                          <div className="mb-6 bg-gradient-to-br from-teal-900/20 to-cyan-900/20 border border-teal-500/20 rounded-lg p-5">
                            <div className="flex items-center gap-3 mb-3">
                              <Sparkles className="w-6 h-6 text-teal-300" />
                              <h5 className="text-lg font-semibold text-teal-200">Houses Overview</h5>
                            </div>
                            <p className="text-gray-300 leading-relaxed">{astrologyResults.house_cusps_overview}</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {astrologyResults.house_cusps.map((cusp, index) => {
                            const zodiacSymbols = {
                              'Aries': '♈', 'Taurus': '♉', 'Gemini': '♊', 'Cancer': '♋',
                              'Leo': '♌', 'Virgo': '♍', 'Libra': '♎', 'Scorpio': '♏',
                              'Sagittarius': '♐', 'Capricorn': '♑', 'Aquarius': '♒', 'Pisces': '♓'
                            };
                            
                            const sign = cusp.sign || cusp.zodiac_sign;
                            const symbol = zodiacSymbols[sign] || '';
                            
                            return (
                              <div key={index} className="bg-slate-800/40 p-3 rounded-lg text-center hover:bg-slate-800/60 transition-colors">
                                <p className="text-blue-300 text-sm mb-1">House {cusp.house || index + 1}</p>
                                <div className="text-3xl my-2">{symbol}</div>
                                <p className="text-white font-semibold">{sign}</p>
                                <p className="text-gray-400 text-xs mt-1">{cusp.degree || cusp.longitude}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-4 justify-center pt-6 border-t border-blue-500/30">
                      <Button
                        onClick={handleAstrologyPDFDownload}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                      >
                        <Gift className="w-5 h-5 mr-2" />
                        Download Report (PDF)
                      </Button>
                      <Button
                        onClick={() => setAstrologyResults(null)}
                        variant="outline"
                        className="border-blue-500/50 text-blue-200 hover:bg-blue-500/20"
                      >
                        Generate New Chart
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save to Profile Button - Shows when both reports are generated */}
            {numerologyResults && astrologyResults && (
              <Card className="bg-gradient-to-r from-amber-900/40 to-orange-900/40 border-amber-500/30 backdrop-blur-sm mt-8">
                <CardContent className="pt-6">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center mb-2">
                      <Sparkles className="w-6 h-6 text-amber-400 mr-2" />
                      <h3 className="text-2xl font-bold text-amber-200">Share Your Cosmic Connection</h3>
                      <Sparkles className="w-6 h-6 text-amber-400 ml-2" />
                    </div>
                    <p className="text-amber-100 max-w-2xl mx-auto">
                      Save your Cosmic Connection to your Public Profile and let others discover your celestial essence. 
                      Your Big Three signs and Life Path will be beautifully displayed for all to see.
                    </p>
                    <Button
                      onClick={handleSaveToProfile}
                      size="lg"
                      className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold text-lg px-8 py-6 mt-4"
                    >
                      <Save className="w-6 h-6 mr-3" />
                      Save to Public Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </SubscriptionGate>
  );
};

export default CosmicCodes;
